import { url, tokenRef } from "api";

const headers = () => {
  const h = { "Content-Type": "application/json" };
  const t = localStorage.getItem(tokenRef);
  if (t) h["Authorization"] = `Bearer ${t}`;
  return h;
};
const handle = async (r) => {
  const d = await r.json();
  if (!r.ok) { const e = new Error(d.err || "Request failed"); e.status = r.status; throw e; }
  return d;
};

// `asOf` (ISO date) reads the map that was current on that day instead
// of the live one: a superseded zone is retired rather than deleted, so
// the drawing people were actually building to still resolves.
export const listAreas = async (projectId, status, asOf) => {
  const qs = [];
  if (status) qs.push(`status=${encodeURIComponent(status)}`);
  if (asOf) qs.push(`asOf=${encodeURIComponent(asOf)}`);
  return handle(await fetch(
    `${url}/projects/${projectId}/areas${qs.length ? `?${qs.join("&")}` : ""}`,
    { headers: headers() }
  ));
};

// GET /areas answers { areas, canEdit }, and the Areas page wants the
// whole object. Everything that only needs the LIST goes through this -
// reading `.items` (which does not exist) silently produced an empty
// picker and an "area no longer approved" label on rows that were filed
// perfectly well.
export const listAreaRows = async (projectId, status) => {
  const r = await listAreas(projectId, status);
  if (Array.isArray(r)) return r;
  return (r && Array.isArray(r.areas)) ? r.areas : [];
};

export const listPendingAreas = async (projectId) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/pending`, { headers: headers() }));

export const createArea = async (projectId, body) =>
  handle(await fetch(`${url}/projects/${projectId}/areas`, { method: "POST", headers: headers(), body: JSON.stringify(body) }));

export const updateArea = async (projectId, id, body) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/${id}`, { method: "PATCH", headers: headers(), body: JSON.stringify(body) }));

export const deleteArea = async (projectId, id) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/${id}`, { method: "DELETE", headers: headers() }));

export const decideArea = async (projectId, id, decision) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/${id}/decide`, { method: "POST", headers: headers(), body: JSON.stringify({ decision }) }));

// Park a sheet for a Claude session to read the areas off it.
export const extractAreas = async (projectId, body) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/extract`, { method: "POST", headers: headers(), body: JSON.stringify(body) }));

// Drawings on this project that can back an area page (issue mirrors
// excluded; a PDF hands back its rendered first page as `url`).
export const listAreaSheets = async (projectId) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/sheets`, { headers: headers() }));

// What has been drawn on this project and when.
export const areaTimeline = async (projectId) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/timeline`, { headers: headers() }));

// Bring a drawing in: it is filed in Document Control for the project,
// then rendered here.
export const importAreaSheet = async (projectId, file, name) => {
  const fd = new FormData();
  fd.append("file", file);
  if (name) fd.append("name", name);
  const t = localStorage.getItem(tokenRef);
  const r = await fetch(`${url}/projects/${projectId}/areas/import-sheet`, {
    method: "POST",
    headers: t ? { Authorization: `Bearer ${t}` } : {},
    body: fd,
  });
  return handle(r);
};

// How much of the project sits inside an area, and everything that
// sits in none.
export const areaCoverage = async (projectId) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/coverage`, { headers: headers() }));

// Put items into an area by hand.
export const assignToArea = async (projectId, areaId, body) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/${areaId}/assign`, {
    method: "POST", headers: headers(), body: JSON.stringify(body),
  }));

// ---------------------------------------------------------- map revisions
//
// A re-issued drawing is not a second set of zones; it is the same zones
// drawn again. The API works out which new area replaces which old one
// and parks it as a proposal - every pairing carries the reason it was
// made, and the owner decides row by row.

export const listAreaMigrations = async (projectId, status) =>
  handle(await fetch(
    `${url}/projects/${projectId}/areas/migrations${status ? `?status=${status}` : ""}`,
    { headers: headers() }
  ));

// Recompute it by hand - for a sheet whose zones were drawn rather than
// extracted, or after the old map was edited.
export const planAreaMigration = async (projectId, docControlId) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/migrations/plan`, {
    method: "POST", headers: headers(), body: JSON.stringify({ docControlId }),
  }));

// `rows` are OVERRIDES: anything not named keeps the action the proposal
// came with, so agreeing with the whole thing sends only the decision.
export const decideAreaMigration = async (projectId, id, decision, rows) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/migrations/${id}/decide`, {
    method: "POST", headers: headers(), body: JSON.stringify({ decision, rows }),
  }));

// Bring a retired zone back. Retiring is history, not deletion - "no
// match on the new sheet" is a judgement about a drawing, and a zone
// left off one page of a set is missing rather than gone.
export const restoreArea = async (projectId, id) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/${id}/restore`, {
    method: "POST", headers: headers(), body: JSON.stringify({}),
  }));

// The days on which the MAP changed - a zone validated, a zone retired -
// so "as of" offers the moments that mean something rather than an open
// calendar.
export const areaHistory = async (projectId) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/history`, { headers: headers() }));

// What each colour MEANS on this project. An area's colour is read off
// the drawing; the legend is what makes the same red on two re-issues
// the same statement rather than a coincidence.
export const getAreaLegend = async (projectId) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/legend`, { headers: headers() }));

export const saveAreaLegend = async (projectId, entries) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/legend`, {
    method: "PUT", headers: headers(), body: JSON.stringify({ entries }),
  }));

// LEVELS & AREAS PAGES (owner, 2026-10-10). A page is a level: its name is
// the level everywhere one is picked, it has its own drawing, and its areas
// are its own or the first page's. A project that predates pages is shown
// pages derived from its drawings (`saved: false`, ids "virtual:N"); the
// first change saves them.
export const listLevels = async (projectId) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/levels`, { headers: headers() }));

export const addLevel = async (projectId, body = {}) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/levels`, { method: "POST", headers: headers(), body: JSON.stringify(body) }));

// { name?, areasFrom?: "own" | "first", sheetDocControlId?, sheetPage? }
export const updateLevel = async (projectId, pageId, body) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/levels/${encodeURIComponent(pageId)}`, { method: "PATCH", headers: headers(), body: JSON.stringify(body) }));

export const removeLevel = async (projectId, pageId) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/levels/${encodeURIComponent(pageId)}`, { method: "DELETE", headers: headers() }));

// One level per page of a drawing set: page 1 on the first level, page 2 on
// the second, a "Level N" made for each page past the last. The answer says
// what was set, created and kept.
export const levelsFromSheet = async (projectId, docControlId) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/levels/from-sheet`, { method: "POST", headers: headers(), body: JSON.stringify({ docControlId }) }));

// SYSTEMS PER AREA (owner, 2026-10-13). One read for the whole project:
// every live zone's systems, what its tagged model elements suggest (with
// counts) and the categories that map to no system. `fresh` waits for a
// recompute when the first answer says `refreshing`.
export const listAreaSystems = async (projectId, { fresh = false } = {}) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/systems${fresh ? "?fresh=1" : ""}`, { headers: headers() }));

// { add: [names], remove: [names] }
export const setAreaSystems = async (projectId, areaId, body) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/${areaId}/systems`, { method: "PUT", headers: headers(), body: JSON.stringify(body) }));

// { names: [...] } | { all: true }
export const acceptAreaSystems = async (projectId, areaId, body) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/${areaId}/systems/accept`, { method: "POST", headers: headers(), body: JSON.stringify(body) }));

// { name, undo? }
export const dismissAreaSystem = async (projectId, areaId, body) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/${areaId}/systems/dismiss`, { method: "POST", headers: headers(), body: JSON.stringify(body) }));

// Systems per area split by level page - for the print and Excel export.
export const listAreaSystemsByLevel = async (projectId) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/systems/by-level`, { headers: headers() }));

// Issue area labels and tags, with the areas each lands in - the unmatched
// ones are offered as an area's "Also known as".
export const listIssueLabels = async (projectId) =>
  handle(await fetch(`${url}/projects/${projectId}/areas/issue-labels`, { headers: headers() }));
